import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain';
import type {
  CorridorBufferCacheRecord,
  CorridorBufferPersistAdapter,
} from './corridor-buffer-persist-types.js';

const DB_NAME = 'gpx-spa-corridor-buffer-cache';
const DB_VERSION = 1;
const STORE = 'entries';
const SCHEMA_VERSION = 1;

type CompressionFormat = 'gzip+json' | 'json';

type IdbRow = {
  id: string;
  shipmentId: number;
  inputHash: string;
  expiresAt: number;
  algorithmVersion: number;
  schemaVersion: number;
  format: CompressionFormat;
  payload: ArrayBuffer;
};

function compositeKey(shipmentId: number, inputHash: string): string {
  return `${shipmentId}:${inputHash}`;
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void =>
      reject(tx.error ?? new Error('IDB transaction failed'));
    tx.onabort = (): void =>
      reject(tx.error ?? new Error('IDB transaction aborted'));
  });
}

async function gzipUtf8String(text: string): Promise<Uint8Array> {
  if (typeof CompressionStream === 'undefined') {
    return new TextEncoder().encode(text);
  }
  const input = new TextEncoder().encode(text);
  const stream = new CompressionStream('gzip');
  const writer = stream.writable.getWriter();
  void writer.write(input);
  void writer.close();
  const buf = await new Response(stream.readable).arrayBuffer();
  return new Uint8Array(buf);
}

async function gunzipToUtf8(bytes: Uint8Array): Promise<string> {
  if (typeof DecompressionStream === 'undefined') {
    return new TextDecoder().decode(bytes);
  }
  const stream = new DecompressionStream('gzip');
  const writer = stream.writable.getWriter();
  void writer.write(new Uint8Array(bytes));
  void writer.close();
  const buf = await new Response(stream.readable).arrayBuffer();
  return new TextDecoder().decode(buf);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = (): void =>
      reject(req.error ?? new Error('indexedDB.open failed'));
    req.onsuccess = (): void => resolve(req.result);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
  });
}

async function decodeRowPayload(
  row: IdbRow,
): Promise<GeoJsonFeatureCollection> {
  const u8 = new Uint8Array(row.payload);
  const json =
    row.format === 'gzip+json'
      ? await gunzipToUtf8(u8)
      : new TextDecoder().decode(u8);
  const parsed = JSON.parse(json) as {
    featureCollection: GeoJsonFeatureCollection;
  };
  return parsed.featureCollection;
}

async function encodePayload(fc: GeoJsonFeatureCollection): Promise<{
  format: CompressionFormat;
  payload: ArrayBuffer;
}> {
  const json = JSON.stringify({ featureCollection: fc });
  if (typeof CompressionStream !== 'undefined') {
    const gz = await gzipUtf8String(json);
    return { format: 'gzip+json', payload: gz.buffer as ArrayBuffer };
  }
  const raw = new TextEncoder().encode(json);
  return { format: 'json', payload: raw.buffer as ArrayBuffer };
}

async function deleteByCompositeKey(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(id);
  await transactionDone(tx);
  db.close();
}

async function putRow(row: IdbRow): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).put(row);
  await transactionDone(tx);
  db.close();
}

async function loadAllRows(): Promise<IdbRow[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  const store = tx.objectStore(STORE);
  const rows: IdbRow[] = [];
  await new Promise<void>((resolve, reject) => {
    const req = store.openCursor();
    req.onerror = (): void =>
      reject(req.error ?? new Error('IDB cursor failed'));
    req.onsuccess = (): void => {
      const cursor = req.result;
      if (!cursor) {
        resolve();
        return;
      }
      rows.push(cursor.value as IdbRow);
      cursor.continue();
    };
  });
  await transactionDone(tx);
  db.close();
  return rows;
}

async function clearExpiredRows(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const now = Date.now();
  await new Promise<void>((resolve, reject) => {
    const req = store.openCursor();
    req.onerror = (): void =>
      reject(req.error ?? new Error('IDB cursor failed'));
    req.onsuccess = (): void => {
      const cursor = req.result;
      if (!cursor) {
        resolve();
        return;
      }
      const row = cursor.value as IdbRow;
      if (row.expiresAt < now) {
        cursor.delete();
      }
      cursor.continue();
    };
  });
  await transactionDone(tx);
  db.close();
}

export function createCorridorBufferIndexedDbAdapter(): CorridorBufferPersistAdapter {
  const adapter: CorridorBufferPersistAdapter = {
    async get(
      shipmentId: number,
      inputHash: string,
    ): Promise<CorridorBufferCacheRecord | null> {
      const id = compositeKey(shipmentId, inputHash);
      const db = await openDb();
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const row = await new Promise<IdbRow | undefined>((resolve, reject) => {
        const g = store.get(id);
        g.onerror = (): void => reject(g.error ?? new Error('IDB get failed'));
        g.onsuccess = (): void => resolve(g.result as IdbRow | undefined);
      });
      await transactionDone(tx);
      db.close();

      if (!row) return null;
      if (row.expiresAt < Date.now()) {
        await deleteByCompositeKey(id);
        return null;
      }
      if (row.schemaVersion !== SCHEMA_VERSION) {
        await deleteByCompositeKey(id);
        return null;
      }
      const featureCollection = await decodeRowPayload(row);
      return {
        inputHash: row.inputHash,
        algorithmVersion: row.algorithmVersion,
        expiresAt: row.expiresAt,
        featureCollection,
      };
    },

    async set(
      shipmentId: number,
      record: CorridorBufferCacheRecord,
    ): Promise<void> {
      const id = compositeKey(shipmentId, record.inputHash);
      const { format, payload } = await encodePayload(record.featureCollection);
      const row: IdbRow = {
        id,
        shipmentId,
        inputHash: record.inputHash,
        expiresAt: record.expiresAt,
        algorithmVersion: record.algorithmVersion,
        schemaVersion: SCHEMA_VERSION,
        format,
        payload,
      };

      try {
        await putRow(row);
      } catch (e) {
        const name = e instanceof DOMException ? e.name : '';
        if (name === 'QuotaExceededError') {
          await adapter.clearExpired();
          const rows = await loadAllRows();
          rows.sort((a, b) => a.expiresAt - b.expiresAt);
          const removeCount = Math.max(1, Math.floor(rows.length * 0.25));
          for (let i = 0; i < removeCount && i < rows.length; i++) {
            await deleteByCompositeKey(rows[i]!.id);
          }
          await putRow(row);
        } else {
          throw e;
        }
      }
    },

    async remove(shipmentId: number, inputHash: string): Promise<void> {
      await deleteByCompositeKey(compositeKey(shipmentId, inputHash));
    },

    async clearExpired(): Promise<void> {
      await clearExpiredRows();
    },
  };

  return adapter;
}
